import { useState, useMemo, useEffect } from "react";
import { User, Plus, Globe, Timer } from "lucide-react";
import { Button } from "../../../ui/button";
import { Input } from "../../../ui/input";
import { Badge } from "../../../ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import { Device } from "../../interfaces/EndpointInterfaces";
import useUserStore from "../../../../services/userStore";
import { URLControl } from "../resourceInterface";
import { addURL, deleteTheURL, fetchUrlControlData, fetchUrlControlDataWithProfile, updateURLTrustLevel, updateURLTrustLevelForProfile } from "../Api/resourceApi";
import { myLocalStorage } from "../../../StorageHelper";
import { Skeleton } from "../../interfaces/CommonInterfaces";
import { getInheritedEndpointResourceStatusValue, getInheritedEndpointResourceStatusValueForURL, labelMapping } from "../helper";
import { RadioGroup, RadioGroupItem } from "../../../ui/radio-group";
import { Label } from "../../../ui/label";
import { toast } from "../../../../hooks/use-toast";
import ConfirmationDialog from "../../Endpoints/ConfirmationDialog";

export interface URLControlTabProps {
  selectedUser: string | null;
  onUserSelect: (userId: string | null) => void;
  users: any[];
  device: Device;
  profileId: number;
  os: string;
  fromDetails: boolean | false;
}

type Status = "Allowed" | "Denied" | "Timed" | "Inherited";

export default function URLControlTab({ selectedUser, onUserSelect, users, device, profileId, os, fromDetails }: URLControlTabProps) {
  const [selectedUrl, setSelectedUrl] = useState<URLControl | null>(null);
  const [urlSearch, setUrlSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [urlStatuses, setUrlStatuses] = useState<Record<string, Record<number, string>>>({});
  const [newUrlInput, setNewUrlInput] = useState("");
  const [timerData, setTimerData] = useState<Record<number, { remainingMinutes: number; totalMinutes: number }>>({});
  const [urlControls, setUrlControls] = useState<URLControl[]>([]);
  const userRole = useUserStore((state) => state.user.role);
  const tenantName = myLocalStorage.getItem("latestTenant")?.tenantName || "";
  const [loading, setLoading] = useState(false);
  const [urlFilter, setUrlFilter] = useState("ALL");
  const { role, email } = useUserStore((state) => state.user);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const getUrlDisplayName = (url: URLControl) => url.urlName?.trim() || url.name?.trim() || "Unnamed URL";

  const normalizeStatus = (status: any): string | undefined => {
    const normalizedStatus = String(status ?? "").toUpperCase();
    if (!normalizedStatus) return undefined;

    if (normalizedStatus === "ENABLED" || normalizedStatus === "ALLOWED") return "ENABLED";
    if (normalizedStatus === "DISABLED" || normalizedStatus === "DENIED" || normalizedStatus === "BLOCKED") return "DISABLED";
    if (normalizedStatus === "DYNAMIC" || normalizedStatus === "TIMED") return "DYNAMIC";
    if (normalizedStatus === "INHERITED") return "INHERITED";

    return normalizedStatus;
  };

  const fetchURLs = async (isFirst = false) => {
    setLoading(isFirst);
    try {
      let response: URLControl[] = [];
      if (selectedUser === "system") {
        response = await fetchUrlControlDataWithProfile(profileId, os);
      } else {
        response = await fetchUrlControlData(tenantName, device?.id, selectedUser || "");
      }
      setUrlControls(response);
      const timers: Record<
        number,
        { remainingMinutes: number; totalMinutes: number }
      > = {};
      response.forEach((item) => {
        const remainingSeconds = Number(item.remainingTime);
        if (Number.isFinite(remainingSeconds) && remainingSeconds > 0) {
          const remainingMinutes = Math.ceil(remainingSeconds / 60);
          const totalMinutes = remainingMinutes;
          timers[item.urlId] = { remainingMinutes, totalMinutes };
        }
      });

      setTimerData(timers);
    } catch {
      setUrlControls([]);
      setTimerData({});
    }
    setLoading(false);
  };

  const handleUrlStatusChange = async (urlId: number, newStatus: string) => {
    if (!selectedUser) return;
    const normalizedStatus = newStatus === "BLOCKED" ? "DISABLED" : newStatus;
    const isSystemUser = selectedUser === "system";

    const resourceMapping: Record<string, string> = {
      ENABLED: "ALLOWED",
      DISABLED: "BLOCKED",
      DYNAMIC: "DYNAMIC",
      INHERITED: "INHERITED",
    };

    setUrlStatuses(prev => ({
      ...prev,
      [selectedUser]: {
        ...(prev[selectedUser] ?? {}),
        [urlId]: normalizedStatus
      }
    }));

    setUrlControls((prev) =>
      prev.map((url) =>
        url.urlId === urlId
          ? {
            ...url,
            groupStatus: normalizedStatus,
            ...(isSystemUser
              ? {
                trustLevel: resourceMapping[normalizedStatus] || normalizedStatus,
                profileResource: resourceMapping[normalizedStatus] || "",
              }
              : {}),
          }
          : url
      )
    );

    setSelectedUrl((prev) => {
      if (!prev || prev.urlId !== urlId) return prev;
      return {
        ...prev,
        groupStatus: normalizedStatus,
        ...(isSystemUser
          ? {
            trustLevel: resourceMapping[normalizedStatus] || normalizedStatus,
            profileResource: resourceMapping[normalizedStatus] || "",
          }
          : {}),
      };
    });

    if (selectedUser === "system") {
      const profilePayload = {
        urlId,
        profileId,
        groupStatus: normalizedStatus,
        email,
        role: userRole,
        tenantName: tenantName,
      };
      await updateURLTrustLevelForProfile(profilePayload);
    } else {
      const payload = {
        userId: selectedUser,
        urlId,
        groupStatus: normalizedStatus,
        selectedTime: 3600,
        email: email,
        role: userRole,
        computerId: device?.id,
      };
      await updateURLTrustLevel(payload, tenantName);
    }
    await fetchURLs();
  };

  const handleAddURL = async () => {
    await addURL(tenantName, newUrlInput, device?.id, email);
    toast({
      title: "Added URL",
      description: `Added URL for ${newUrlInput}`,
    });
    setNewUrlInput("");
    fetchURLs()
  }

  const deleteURL = async (url: URLControl) => {
    await deleteTheURL(url.urlId);
    toast({
      title: "Deleted URL",
      description: `Deleted URL for ${url.name}`,
    });
    fetchURLs()
    setSelectedUrl(null)
  }


  const getUrlStatus = (url: URLControl) => {
    if (!selectedUser) return normalizeStatus(url.groupStatus) || normalizeStatus(url.profileResource);

    const optimisticStatus = urlStatuses[selectedUser]?.[url.urlId];
    if (optimisticStatus) return optimisticStatus;

    if (selectedUser === "system") {
      const profileStatus = normalizeStatus(url.profileResource);
      const trustStatus = normalizeStatus((url as any)?.trustLevel);

      if (profileStatus === "INHERITED" && trustStatus) {
        return trustStatus;
      }

      return profileStatus || trustStatus;
    }

    return normalizeStatus(url.groupStatus) || normalizeStatus(url.profileResource);
  };

  const getStatus = (url: URLControl) => {
    const inheritedStatus = selectedUser === "system" ? "" : getInheritedEndpointResourceStatusValueForURL(url);
    return labelMapping(getUrlStatus(url) || "", inheritedStatus || "")
  }

  const getUrlSelectStatusValue = (url: URLControl | null | undefined) => {
    if (!url) return undefined;
    const status = getUrlStatus(url);
    if (!status) return undefined;

    const normalizedStatus = normalizeStatus(status) || String(status).toUpperCase();
    if (normalizedStatus === "INHERITED" && selectedUser === "system") {
      const hasCategoryName = Boolean(String((url as any)?.categoryName ?? "").trim());
      if (hasCategoryName) return "INHERITED";

      const inheritedStatus = normalizeStatus((url as any)?.trustLevel || url.profileResource || "");
      if (inheritedStatus === "DISABLED" || inheritedStatus === "DYNAMIC" || inheritedStatus === "ENABLED") return inheritedStatus;
      return "ENABLED";
    }
    return normalizedStatus;
  }

  const getTimerDisplay = (urlId: number) => {
    const timer = timerData[urlId];
    if (!timer) return "0m";
    const remaining = Number(timer.remainingMinutes);
    if (!Number.isFinite(remaining) || remaining <= 0) return "0m";
    const hours = Math.floor(remaining / 60);
    const minutes = remaining % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  useEffect(() => {
    const interval = setInterval(() => {
      setTimerData(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach((key) => {
          const id = Number(key);
          if (updated[id].remainingMinutes > 0) {
            updated[id].remainingMinutes -= 1;
          }
        });
        return updated;
      });
    }, 60000);

    return () => clearInterval(interval);
  }, []);

  // Filter users
  const filteredUsers = useMemo(() => {
    return users.filter(user => user.cn.toLowerCase().includes(userSearch.toLowerCase()));
  }, [users, userSearch]);

  useEffect(() => {
    fetchURLs(true);
  }, [selectedUser, device, profileId, tenantName, os]);


  const filteredURL = useMemo(() => {
    const filterMap: Record<string, string[]> = {
      ALL: ["ALLOWED", "DENIED", "TIMED", "INHERITED"],
      ALLOWED: ["ALLOWED"],
      DENIED: ["DENIED"],
      TIMED: ["TIMED"],
    };
    return urlControls.filter((app) => {
      const appData = app?.name || app?.urlName || "";
      if (!appData) return false;

      const status = getStatus(app);
      const matchesPrivilege = filterMap[urlFilter]?.includes(status) ?? true;
      const matchesSearch = appData
        ?.toLowerCase()
        .includes(urlSearch.toLowerCase());

      return matchesPrivilege && matchesSearch;
    });
  }, [urlControls, urlSearch, urlFilter, urlStatuses, selectedUser]);

  useEffect(() => {
    setSelectedUrl(null);
  }, [urlFilter])

  return (
    <>
      <div className="flex flex-1 overflow-hidden">
        {/* Users List */}
        {selectedUser !== "system" && (
          <div className="w-64 p-4 bg-white border-r dark:bg-background">
            <h3 className="mb-4 text-sm font-medium">Users List</h3>
            <Input
              placeholder="Search users..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              className="mb-3 text-sm"
              data-testid="input-user-search"
            />
            <div className="space-y-3">
              {filteredUsers.map(user => (
                <div
                  key={user.id}
                  className={`flex items-center gap-3 p-2 rounded-md cursor-pointer transition-colors ${String(selectedUser) === String(user.id)
                    ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'hover-elevate text-muted-foreground'}`}
                  onClick={() => { onUserSelect(user.id); setSelectedUrl(null); setUrlSearch(""); }}
                  data-testid={`user-${user.id}`}
                >
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted">
                    <User className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div className="text-sm font-medium">{user.cn}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* URL List */}
        <div className="flex-1 p-4 overflow-hidden bg-white border-r dark:bg-background">
          <h3 className="mb-4 text-sm font-medium">URL Control</h3>

          {/* Add URL */}
          <div className="p-3 mb-4 rounded-md bg-muted/50">
            <label className="block mb-2 text-xs text-muted-foreground">Add New URL</label>
            <div className="flex gap-2">
              <Input
                placeholder="Enter URL..."
                value={newUrlInput}
                onChange={e => setNewUrlInput(e.target.value)}
                className="flex-1 text-sm"
                disabled={!selectedUser && selectedUser !== "system"}
              />
              <Button disabled={!selectedUser || !newUrlInput.trim()} size="sm" onClick={handleAddURL}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>

          <Input
            placeholder="Search URLs..."
            value={urlSearch}
            onChange={e => setUrlSearch(e.target.value)}
            className="mb-3 text-sm"
            disabled={!selectedUser}
          />

          <div className="flex items-center gap-4 mb-2">
            <span className="text-sm font-medium text-muted-foreground">Privilege</span>
            <RadioGroup value={urlFilter} onValueChange={setUrlFilter} className="flex gap-4">
              {["ALL", "ALLOWED", "DENIED", "TIMED"].map((v) => (
                <div key={v} className="flex items-center space-x-2">
                  <RadioGroupItem value={v} id={`filter-${v.toLowerCase()}`} />
                  <Label htmlFor={`filter-${v.toLowerCase()}`} className="text-sm">{v}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <div className="space-y-2">
            {loading ? Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="p-3 border rounded-md">
                <Skeleton className="w-3/4 h-4 mb-2" />
                <Skeleton className="w-1/2 h-3" />
              </div>
            )) : (selectedUser || selectedUser === "system") ? (
              filteredURL.length > 0 ? filteredURL
                .filter(url => getUrlDisplayName(url).toLowerCase().includes(urlSearch.toLowerCase()))
                .map(url => (
                  <div
                    key={url.urlId}
                    className={`p-3 rounded-md cursor-pointer transition-colors border ${selectedUrl?.urlId === url.urlId
                      ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700'
                      : 'hover-elevate border-border'}`}
                    onClick={() => setSelectedUrl(url)}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center flex-1 min-w-0 gap-3">
                        <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 rounded bg-muted">
                          <Globe className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-sm font-medium break-all">{getUrlDisplayName(url)}</div>
                        </div>
                      </div>
                      <div className="flex items-center flex-shrink-0 gap-3">
                        <Badge
                          variant={
                            getStatus(url) === "ALLOWED"
                              ? "default"
                              : getStatus(url) === "DENIED"
                                ? "destructive"
                                : getStatus(url) === "TIMED"
                                  ? "secondary"
                                  : "default"}
                          className="text-xs min-w-[60px] text-center"
                        >
                          {getStatus(url)}
                        </Badge>
                        {getStatus(url) === "TIMED" && selectedUser !== "system" && (
                          <div className="flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 font-medium min-w-[50px]">
                            <Timer className="w-3 h-3" />
                            <span>{getTimerDisplay(url.urlId)}</span>
                          </div>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-6 h-6 p-0 text-xs text-muted-foreground hover:text-destructive"
                          onClick={() => {
                            setShowDeleteConfirmation(true)
                          }}
                        >
                          ✕
                        </Button>
                      </div>
                    </div>
                  </div>
                )) : <div className="py-8 text-sm text-center text-muted-foreground">No URL found</div>
            ) : (
              <div className="py-8 text-sm text-center text-muted-foreground">Select a user to manage URLs</div>
            )}
          </div>
        </div>

        {/* URL Properties Panel */}
        <div className="p-4 overflow-y-auto bg-white border-l w-80 dark:bg-background">
          {selectedUrl ? (
            <div className="space-y-6">
              <h3 className="mb-2 font-mono text-lg font-semibold break-all">{getUrlDisplayName(selectedUrl)}</h3>

              <div>
                <label className="block mb-2 text-xs font-medium tracking-wide uppercase text-muted-foreground">Access Control</label>
                <Select
                  value={getUrlSelectStatusValue(selectedUrl)}
                  onValueChange={value => handleUrlStatusChange(selectedUrl.urlId, value)}
                >
                  <SelectTrigger className="h-auto min-h-8 text-xs w-55 border-blue-200 whitespace-normal break-words dark:border-blue-700 bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-300 font-medium hover:bg-blue-100 dark:hover:bg-blue-900/40 focus:ring-2 focus:ring-blue-500 focus:border-blue-500">

                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ENABLED">ALLOWED</SelectItem>
                    <SelectItem value="DISABLED">DENIED</SelectItem>
                    <SelectItem value="DYNAMIC">TIMED</SelectItem>
                    {(selectedUser !== "system" || Boolean(String((selectedUrl as any)?.categoryName ?? "").trim())) && (
                      <SelectItem value="INHERITED">INHERITED {getInheritedEndpointResourceStatusValueForURL(selectedUrl)}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full text-sm text-center text-muted-foreground">
              <div className="mb-2">🌐</div>
              <div>No URL selected</div>
            </div>
          )}
        </div>
      </div>
      <ConfirmationDialog
        open={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
        title="Delete URL"
        description={`Are you sure, you want to delete this URL - ${selectedUrl?.name}?`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
        onConfirm={() => {
          if (selectedUrl)
            deleteURL(selectedUrl);
        }}
      />
    </>
  );
}
